import { h, toast } from './dom';
import { go, session } from './session';
import { sound } from './sound';
import { socket } from './ws';
import { api } from './api';

const ORIGINAL_TITLE = 'Tanky';
let titleTimer: number | null = null;
let titleToggle = false;
let activeNotification: Notification | null = null;
let initialized = false;

/** Sada již ohlášených tahů: `${gameId}:${updatedAt}`, abychom nehlásili stejný tah opakovaně */
const notifiedTurns = new Set<string>();
const permListeners = new Set<(perm: NotificationPermission | 'unsupported') => void>();

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  try {
    const res = await Notification.requestPermission();
    for (const l of permListeners) l(res);
    if (res === 'granted') {
      toast('🔔 Upozornění zapnuta – dáme ti vědět, až budeš na řadě.');
    } else if (res === 'denied') {
      toast('Notifikace jsou v prohlížeči zakázány.', true);
    }
    return res;
  } catch {
    return 'denied';
  }
}

export function onPermissionChange(cb: (perm: NotificationPermission | 'unsupported') => void): () => void {
  permListeners.add(cb);
  return () => permListeners.delete(cb);
}

/** Spustí střídavé blikání titulku záložky v prohlížeči, dokud se uživatel do okna nevrátí */
export function startTitleFlash(text = '🔔 Jsi na tahu!') {
  if (titleTimer) return;
  titleToggle = true;
  document.title = `${text} · ${ORIGINAL_TITLE}`;
  titleTimer = window.setInterval(() => {
    titleToggle = !titleToggle;
    document.title = titleToggle ? `${text} · ${ORIGINAL_TITLE}` : ORIGINAL_TITLE;
  }, 1000);
}

/** Zastaví blikání záložky a zavře aktivní notifikaci */
export function stopTitleFlash() {
  if (titleTimer) {
    clearInterval(titleTimer);
    titleTimer = null;
  }
  document.title = ORIGINAL_TITLE;
  if (activeNotification) {
    try {
      activeNotification.close();
    } catch {
      /* ignorovat */
    }
    activeNotification = null;
  }
}

export interface TurnAlertOptions {
  gameId?: string;
  opponents?: string[];
  inCurrentGame?: boolean;
}

/**
 * Upozorní hráče, že je na řadě:
 * 1. Zvukový signál (sound.yourTurn)
 * 2. Blikání záložky okna, pokud není hra v popředí
 * 3. Systémová Web notifikace (pokud jsou povoleny)
 * 4. Klikatelný in-app toast, pokud je hráč na jiné stránce aplikace
 */
export function notifyMyTurn(opt: TurnAlertOptions = {}) {
  const isHidden = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());

  // 1. Zvukový signál
  sound.yourTurn();

  // 2. Blikání názvu v záložce, pokud hráč nemá okno zrovna aktivní
  if (isHidden) {
    startTitleFlash();
  }

  const oppText = opt.opponents?.length ? `proti: ${opt.opponents.join(', ')}` : '';
  const body = oppText ? `Hra ${oppText} čeká na tvůj výstřel!` : 'Je řada na tobě, zamiř a vystřel!';

  // 3. Systémová notifikace (pokud jsou povoleny a hráč nemá okno hry aktivní před sebou)
  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
    if (isHidden || !opt.inCurrentGame) {
      try {
        const notif = new Notification('Tanky · Jsi na tahu! 🎯', {
          body,
          icon: '/icon.svg',
          tag: `tanky-turn-${opt.gameId ?? 'current'}`,
        });
        activeNotification = notif;
        notif.onclick = () => {
          window.focus();
          if (opt.gameId) go(`/game/${opt.gameId}`);
          notif.close();
          stopTitleFlash();
        };
      } catch {
        /* ignorovat případné omezení prohlížeče */
      }
    }
  }

  // 4. Pokud hráč není přímo v dané hře (je v lobby, v profilu atd.), zobrazíme klikatelný in-app toast
  if (!opt.inCurrentGame && opt.gameId) {
    const toastEl = h(
      'div',
      {
        class: 'toast clickable',
        onclick: () => {
          go(`/game/${opt.gameId}`);
          stopTitleFlash();
        },
      },
      h('b', {}, '🎯 Jsi na tahu! '),
      oppText ? h('span', { class: 'muted' }, `(${oppText}) `) : null,
      h('span', { style: { textDecoration: 'underline', color: 'var(--accent, #ff7a45)' } }, 'Přejít do hry →'),
    );
    const box = document.getElementById('toasts');
    if (box) {
      box.append(toastEl);
      setTimeout(() => toastEl.remove(), 7000);
    }
  }
}

/** Tlačítko pro stav/zapnutí notifikací (pro použití v liště lobby i ve hře). */
export function notificationButton(className: string, cleanups?: (() => void)[]): HTMLButtonElement {
  const btn = h('button', { class: className, type: 'button' }) as HTMLButtonElement;
  const update = () => {
    const p = getNotificationPermission();
    if (p === 'unsupported') {
      btn.hidden = true;
      return;
    }
    btn.hidden = false;
    if (p === 'granted') {
      btn.textContent = '🔔';
      btn.style.opacity = '1';
      btn.title = 'Upozornění na tah v prohlížeči jsou zapnuta';
    } else if (p === 'denied') {
      btn.textContent = '🔕';
      btn.style.opacity = '0.5';
      btn.title = 'Notifikace jsou v nastavení prohlížeče zablokovány';
    } else {
      btn.textContent = '🔔';
      btn.style.opacity = '0.6';
      btn.title = 'Zapnout upozornění na tah, když jsi mimo hru';
    }
  };
  btn.onclick = async () => {
    const p = getNotificationPermission();
    if (p === 'default') {
      await requestNotificationPermission();
    } else if (p === 'granted') {
      toast('🔔 Upozornění na tah v prohlížeči jsou aktivní.');
    } else if (p === 'denied') {
      toast('Notifikace jsou zakázány v nastavení prohlížeče.', true);
    }
    update();
  };
  update();
  const off = onPermissionChange(update);
  cleanups?.push(off);
  return btn;
}

/** Zkontroluje, zda hráč nemá tah v nějaké rozehrané hře */
export async function checkActiveTurns() {
  if (!session.me) return;
  try {
    const { games } = await api.games();
    const myId = session.me.id;
    const currentPath = location.hash.replace(/^#/, '');

    for (const g of games) {
      if (g.status === 'playing' && g.currentPlayerId === myId) {
        const key = `${g.id}:${g.updatedAt}`;
        if (notifiedTurns.has(key)) continue;
        notifiedTurns.add(key);

        const inThisGame = currentPath === `/game/${g.id}`;
        const isHidden = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());
        if (!inThisGame || isHidden) {
          const opponents = g.players.filter((p) => p.id !== myId).map((p) => p.name);
          notifyMyTurn({ gameId: g.id, opponents, inCurrentGame: inThisGame });
        }
      }
    }
  } catch {
    /* ignorovat síťové chyby na pozadí */
  }
}

/** Globální inicializace hlídání tahů a obnovy titulku při zaměření okna. */
export function initNotifications() {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;

  const onFocus = () => stopTitleFlash();
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) stopTitleFlash();
  });

  // Poslech WebSocket zpráv o změnách ve hrách (např. když je hráč v lobby nebo na jiné stránce)
  socket.on((msg) => {
    if (msg.t === 'games_changed' && session.me) {
      checkActiveTurns();
    }
  });

  // Pravidelná kontrola každých 30 s pro případ zpožděných tahů
  window.setInterval(() => {
    if (session.me) checkActiveTurns();
  }, 30000);
}
